"use client"

import { useState, useEffect } from "react"
import "./OrderConfirmation.css"
import ConfirmAnimation from "../Assets/confirm.json"
import { getDatabase, ref, get, set } from "firebase/database" // Changed to Realtime Database
import { getAuth } from "firebase/auth"
import Lottie from "lottie-react"

export default function OrderConfirmation() {
  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [orderStatus, setOrderStatus] = useState("processing")
  const [orderNumber, setOrderNumber] = useState("")

  // Get current user
  const auth = getAuth()
  const database = getDatabase() // Use Realtime Database

  useEffect(() => {
    // Fetch order data from localStorage
    const fetchOrderData = () => {
      try {
        const storedOrderData = localStorage.getItem("orderData")
        if (storedOrderData) {
          const parsedData = JSON.parse(storedOrderData)
          setOrderData(parsedData)

          // Set order number from the data
          if (parsedData.orderNumber) {
            setOrderNumber(parsedData.orderNumber)
          }
        }
      } catch (error) {
        console.error("Error fetching order data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrderData()

    // Simulate order status changes for demo purposes
    const statusTimer = setTimeout(() => {
      setOrderStatus("confirmed")

      // Update order status in database if we have an order number
      if (orderNumber && auth.currentUser) {
        try {
          const orderRef = ref(database, `orders/${orderNumber}`)
          get(orderRef).then((snapshot) => {
            if (snapshot.exists()) {
              set(ref(database, `orders/${orderNumber}/status`), "confirmed")
            }
          })
        } catch (error) {
          console.error("Error updating order status:", error)
        }
      }
    }, 3000)

    return () => clearTimeout(statusTimer)
  }, [auth.currentUser, database, orderNumber])

  const getEstimatedDeliveryDate = () => {
    const today = new Date()
    const deliveryDate = new Date(today)
    deliveryDate.setDate(today.getDate() + 3) // Assuming 3-day delivery

    return deliveryDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
  }

  if (loading) {
    return <div className="loading">Loading your order confirmation...</div>
  }

  if (!orderData) {
    return (
      <div className="error-container">
        <h1>Order Not Found</h1>
        <p>We couldn't find your order details. Please contact customer support.</p>
        <button className="primary-button" onClick={() => (window.location.href = "/")}>
          Return to Home
        </button>
      </div>
    )
  }

  return (
    <div className="confirmation-container">
      <div className="confirmation-wrapper">
        <div className="confirmation-header">
          <div className="success-icon">
            <Lottie animationData={ConfirmAnimation} loop={true} />
          </div>
          <h1 className="confirmation-title">Order Confirmed!</h1>
          <p className="confirmation-subtitle">
            Thank you for your order. We've received your payment and are preparing your cookies!
          </p>
        </div>

        <div className="order-status-container">
          <h2 className="section-title">Order Status</h2>
          <div className="order-status-tracker">
            <div
              className={`status-step ${orderStatus === "processing" || orderStatus === "confirmed" ? "active" : ""}`}
            >
              <div className="status-circle"></div>
              <p>Order Placed</p>
            </div>
            <div className="status-line"></div>
            <div className={`status-step ${orderStatus === "confirmed" ? "active" : ""}`}>
              <div className="status-circle"></div>
              <p>Confirmed</p>
            </div>
            <div className="status-line"></div>
            <div className="status-step">
              <div className="status-circle"></div>
              <p>Preparing</p>
            </div>
            <div className="status-line"></div>
            <div className="status-step">
              <div className="status-circle"></div>
              <p>Ready for Pickup</p>
            </div>
            <div className="status-line"></div>
            <div className="status-step">
              <div className="status-circle"></div>
              <p>Completed</p>
            </div>
          </div>
        </div>

        <div className="order-details-grid">
          <div className="order-info-card">
            <h2 className="section-title">Order Information</h2>
            <div className="order-info-content">
              <div className="info-row">
                <span className="info-label">Order Number:</span>
                <span className="info-value">{orderData.orderNumber || orderNumber}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Order Date:</span>
                <span className="info-value">{new Date().toLocaleDateString()}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Estimated Delivery:</span>
                <span className="info-value">{getEstimatedDeliveryDate()}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Payment Method:</span>
                <span className="info-value">Credit Card</span>
              </div>
            </div>
          </div>

          <div className="shipping-info-card">
            <h2 className="section-title">Shipping Information</h2>
            <div className="shipping-info-content">
              <p className="address-line">{orderData.address.street}</p>
              <p className="address-line">
                {orderData.address.city}, {orderData.address.state} {orderData.address.zipCode}
              </p>
              <p className="address-line">Phone: {orderData.address.phone}</p>
            </div>
          </div>
        </div>

        <div className="order-items-card">
          <h2 className="section-title">Order Summary</h2>
          <div className="order-items-list">
            {orderData.items.map((item) => (
              <div key={item.id} className="order-item">
                <div className="item-image">
                  <img src={item.image || "/placeholder.svg"} alt={item.name} />
                </div>
                <div className="item-details">
                  <h3>{item.name}</h3>
                  <p className="item-price">
                    ₹{item.price.toFixed(2)} × {item.quantity}
                  </p>
                </div>
                <div className="item-total">₹{(item.price * item.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>

          <div className="order-summary">
            <div className="summary-row">
              <p className="summary-label">Subtotal</p>
              <p className="summary-value">₹{orderData.totals.subtotal.toFixed(2)}</p>
            </div>
            <div className="summary-row">
              <p className="summary-label">Tax</p>
              <p className="summary-value">₹{orderData.totals.tax.toFixed(2)}</p>
            </div>
            <div className="summary-row">
              <p className="summary-label">Shipping</p>
              <p className="summary-value">₹{orderData.totals.shipping.toFixed(2)}</p>
            </div>
            <div className="separator"></div>
            <div className="summary-row total">
              <p className="total-label">Total</p>
              <p className="total-value">₹{orderData.totals.total.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="track-order-button" onClick={() => (window.location.href = "/tracking")}>
            Track Order
          </button>
          <button className="continue-shopping-button" onClick={() => (window.location.href = "/")}>
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  )
}

